// The scrollbar's thumb shows only while its box scrolls (as macOS does).
//
// The scrollbar is drawn by us (styles/index.css, `::-webkit-scrollbar`) so it
// is equally thin everywhere; drawn so, the system's "gone while not scrolling"
// no longer applies and the thumb stayed (2026-09-27 and again 2026-10-18, the
// person). The thumb is transparent and coloured only while the box that
// scrolls carries `data-scrolling`.
//
// One listener for the whole document (scroll does not bubble, so it is caught
// on the way down). The mark is written twice per scroll, when it starts and
// when it stops, not on every scroll event.

/** How long after the scrolling stops the thumb goes. */
export const SCROLLBAR_HIDE_DELAY_MS = 1000;

export function installScrollbarAutohide(
  target: Document = document,
): () => void {
  // Not on touch devices: their scrollbar is the system's overlay, which
  // `::-webkit-scrollbar` does not reach, so the mark would change nothing and
  // only restyle `<html>` while a page scrolls (a suspect of iOS jumping to the
  // top mid-scroll).
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(hover: none) and (pointer: coarse)").matches
  )
    return () => {};
  const timers = new Map<Element, ReturnType<typeof setTimeout>>();
  const onScroll = (e: Event) => {
    const el =
      e.target instanceof Document ? e.target.documentElement : e.target;
    if (!(el instanceof Element)) return;
    const prev = timers.get(el);
    if (prev === undefined) el.setAttribute("data-scrolling", "");
    else clearTimeout(prev);
    timers.set(
      el,
      setTimeout(() => {
        timers.delete(el);
        el.removeAttribute("data-scrolling");
      }, SCROLLBAR_HIDE_DELAY_MS),
    );
  };
  target.addEventListener("scroll", onScroll, { capture: true, passive: true });
  return () => {
    target.removeEventListener("scroll", onScroll, { capture: true });
    for (const [el, t] of timers) {
      clearTimeout(t);
      el.removeAttribute("data-scrolling");
    }
    timers.clear();
  };
}
